import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, dateTimeOf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { PaperPlaneTilt, Camera } from "@phosphor-icons/react";

const STATUS_CHIP = {
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  under_review: "bg-blue-50 text-blue-700 border-blue-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-red-50 text-red-700 border-red-200",
  more_info: "bg-amber-50 text-amber-800 border-amber-200",
  withdrawn: "bg-slate-100 text-slate-600 border-slate-200",
  posted: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const empty = {
  type: "", student_id: "", details: "", requested_date: "", requested_time: "",
  person_name: "", person_phone: "", person_relationship: "",
};

const RequestCard = ({ r, onReply, onWithdraw }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!note.trim()) return;
    setBusy(true);
    await onReply(r.id, note.trim());
    setBusy(false);
    setNote("");
  };
  return (
    <div className="bg-white rounded-3xl border border-border p-6" data-testid={`request-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-lg font-semibold">{r.type_label}</p>
          <p className="text-sm text-muted-foreground">
            {r.student_name || "General"}{r.requested_date ? ` · ${r.requested_date}` : ""}
            {r.requested_time ? ` at ${r.requested_time}` : ""}
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-bold ${STATUS_CHIP[r.status] || ""}`}
              data-testid={`request-status-${r.id}`}>
          {r.status_label}
        </span>
      </div>

      {r.details && <p className="mt-4 text-sm">{r.details}</p>}
      {r.person_name && (
        <p className="mt-2 text-sm text-muted-foreground">
          Person: <strong>{r.person_name}</strong>
          {r.person_relationship ? ` · ${r.person_relationship}` : ""}
          {r.person_phone ? ` · ${r.person_phone}` : ""}
        </p>
      )}

      <ol className="mt-5 space-y-3 border-l-2 border-border pl-4" data-testid={`request-trail-${r.id}`}>
        {r.trail.map((t, i) => (
          <li key={`${t.at}-${i}`} className="text-sm">
            <p className="font-bold">
              {t.reply ? "Reply" : r.status_label === t.status ? "" : ""}
              {t.note || "Status updated"}
            </p>
            <p className="text-xs text-muted-foreground">{t.by} · {dateTimeOf(t.at)}</p>
          </li>
        ))}
      </ol>

      {r.is_open && (
        <div className="mt-5 space-y-3">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                    placeholder={r.status === "more_info" ? "Answer the office's question…" : "Add a note…"}
                    className="rounded-2xl" data-testid={`reply-input-${r.id}`} />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={send} disabled={busy || !note.trim()}
                    className="rounded-full font-bold bg-[#3B82F6] hover:bg-[#2563EB]"
                    data-testid={`reply-btn-${r.id}`}>
              <PaperPlaneTilt size={15} className="mr-2" /> Send reply
            </Button>
            <Button size="sm" variant="outline" onClick={() => onWithdraw(r.id)}
                    className="rounded-full font-bold" data-testid={`withdraw-btn-${r.id}`}>
              Withdraw request
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ParentRequests() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ ...empty, student_id: params.get("child") || "" });
  const [busy, setBusy] = useState(false);
  const tab = params.get("tab") || "new";

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([api.get("/portal/meta"), api.get("/portal/requests")]);
      setMeta(m.data);
      setRows(r.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(
    () => (meta?.request_types || []).find((t) => t.key === form.type) || null, [meta, form.type]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.type) { toast.error("Choose what you would like to ask for"); return; }
    setBusy(true);
    try {
      await api.post("/portal/requests", form);
      toast.success(selected?.immediate ? "Thank you — the classroom has been told."
                                        : "Request submitted. We will let you know once it is reviewed.");
      setForm({ ...empty });
      await load();
      setParams({ tab: "mine" });
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    setBusy(false);
  };

  const reply = async (id, note) => {
    try {
      await api.post(`/portal/requests/${id}/reply`, { note });
      toast.success("Reply sent");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const withdraw = async (id) => {
    try {
      await api.post(`/portal/requests/${id}/withdraw`);
      toast.success("Request withdrawn");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setConsent = async (sid, consent) => {
    try {
      await api.put(`/portal/photo-consent/${sid}`, { consent });
      toast.success(consent ? "Photos of your child can be shared with you" : "Photo sharing turned off");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!meta) return <p className="text-muted-foreground" data-testid="requests-loading">Loading…</p>;

  const open = rows.filter((r) => r.is_open);
  const closed = rows.filter((r) => !r.is_open);
  const groups = [...new Set((meta.request_types || []).map((t) => t.group))];

  return (
    <div data-testid="parent-requests">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Family Portal</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Requests</h1>
      <p className="mt-3 text-muted-foreground max-w-2xl">
        Ask us for a change instead of making it yourself. Nothing here changes your child's record until a
        staff member approves it — except an absence or late-arrival notice, which we pass to the classroom
        straight away.
      </p>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mt-8">
        <TabsList data-testid="requests-tabs">
          <TabsTrigger value="new" data-testid="tab-new">Make a request</TabsTrigger>
          <TabsTrigger value="mine" data-testid="tab-mine">My requests ({open.length})</TabsTrigger>
          <TabsTrigger value="consent" data-testid="tab-consent">Photo sharing</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-6">
          {meta.children.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="requests-no-children">
              No children are connected to your account yet, so there is nothing to request. Call
              (501) 904-7103 and we will connect you.
            </p>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-3xl border border-border p-6 max-w-2xl space-y-5"
                  data-testid="new-request-form">
              <div>
                <Label className="text-sm font-bold">What do you need?</Label>
                <Select value={form.type} onValueChange={set("type")}>
                  <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="request-type">
                    <SelectValue placeholder="Choose a request" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <div key={g}>
                        <p className="px-2 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{g}</p>
                        {meta.request_types.filter((t) => t.group === g).map((t) => (
                          <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
                {selected?.help && <p className="mt-2 text-xs text-muted-foreground">{selected.help}</p>}
                {selected?.immediate && (
                  <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid="immediate-note">
                    This goes straight to the classroom as parent-reported information.
                  </p>
                )}
                {selected?.notice && (
                  <p className="mt-2 text-xs font-semibold text-amber-700" data-testid="notice-note">
                    Schedule changes need {meta.schedule_notice_days} days of notice.
                  </p>
                )}
                {selected?.health && (
                  <p className="mt-2 text-xs font-semibold text-amber-700" data-testid="health-note">
                    This goes to the health review team — a plan only changes once staff have reviewed it.
                  </p>
                )}
              </div>

              <div>
                <Label className="text-sm font-bold">Which child?</Label>
                <Select value={form.student_id} onValueChange={set("student_id")}>
                  <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="request-child">
                    <SelectValue placeholder="Choose a child" />
                  </SelectTrigger>
                  <SelectContent>
                    {meta.children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {selected?.needs_date && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold">{selected.date_label || "Date"}</Label>
                    <Input type="date" value={form.requested_date}
                           onChange={(e) => set("requested_date")(e.target.value)}
                           className="mt-2 h-12 rounded-xl" data-testid="request-date" />
                  </div>
                  {selected.needs_time && (
                    <div>
                      <Label className="text-sm font-bold">{selected.time_label || "Time"}</Label>
                      <Input type="time" value={form.requested_time}
                             onChange={(e) => set("requested_time")(e.target.value)}
                             className="mt-2 h-12 rounded-xl" data-testid="request-time" />
                    </div>
                  )}
                </div>
              )}

              {selected?.needs_person && (
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-bold">Their name</Label>
                    <Input value={form.person_name} onChange={(e) => set("person_name")(e.target.value)}
                           className="mt-2 h-12 rounded-xl" data-testid="request-person-name" />
                  </div>
                  <div>
                    <Label className="text-sm font-bold">Relationship</Label>
                    <Input value={form.person_relationship}
                           onChange={(e) => set("person_relationship")(e.target.value)}
                           className="mt-2 h-12 rounded-xl" data-testid="request-person-relationship" />
                  </div>
                  <div>
                    <Label className="text-sm font-bold">Phone</Label>
                    <Input value={form.person_phone} onChange={(e) => set("person_phone")(e.target.value)}
                           className="mt-2 h-12 rounded-xl" data-testid="request-person-phone" />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-bold">Anything we should know?</Label>
                <Textarea value={form.details} onChange={(e) => set("details")(e.target.value)} rows={3}
                          className="mt-2 rounded-2xl" data-testid="request-details" />
              </div>

              <Button type="submit" disabled={busy}
                      className="w-full h-12 rounded-full font-bold bg-[#3B82F6] hover:bg-[#2563EB] active:scale-95"
                      style={{ transition: "transform .2s ease, background-color .2s ease" }}
                      data-testid="submit-request">
                {busy ? "Sending…" : "Submit request"}
              </Button>
            </form>
          )}
        </TabsContent>

        <TabsContent value="mine" className="mt-6 space-y-4">
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="requests-empty">
              You have not made any requests yet.
            </p>
          )}
          {open.map((r) => <RequestCard key={r.id} r={r} onReply={reply} onWithdraw={withdraw} />)}
          {closed.length > 0 && (
            <>
              <h2 className="text-base md:text-lg font-bold pt-4">Closed</h2>
              {closed.map((r) => <RequestCard key={r.id} r={r} onReply={reply} onWithdraw={withdraw} />)}
            </>
          )}
        </TabsContent>

        <TabsContent value="consent" className="mt-6">
          <div className="bg-white rounded-3xl border border-border p-6 max-w-2xl" data-testid="consent-panel">
            <div className="flex items-start gap-4">
              <span className="h-11 w-11 rounded-2xl bg-blue-50 text-[#3B82F6] grid place-items-center shrink-0">
                <Camera size={22} weight="duotone" />
              </span>
              <p className="text-sm">
                Choose whether classroom photos that include your child may be shared with you in the portal.
                Until you decide, those photos are held back for a director to review — nothing is shared by
                assumption.
              </p>
            </div>
            <div className="mt-6 space-y-3">
              {meta.children.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-4 rounded-2xl border border-border px-4 py-3">
                  <div>
                    <p className="text-sm font-bold">{c.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {c.media_consent === true ? "Photos may be shared"
                        : c.media_consent === false ? "Photos are not shared"
                        : "No decision recorded yet"}
                    </p>
                  </div>
                  <Switch checked={c.media_consent === true}
                          onCheckedChange={(v) => setConsent(c.id, v)}
                          data-testid={`consent-switch-${c.id}`} />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
